import { formatBoxCode, orderNumberText, withBoxCounts } from './boxLabel';

/**
 * PGSS-5805: print one box's label (or a few), e.g. when a label was damaged in transit. The label page prints every
 * box of the container; "Boxes to print" narrows it. Nothing picked means every box, so the default is unchanged.
 */

/**
 * A stable key for one label: its box code. A label without one (code assignment failed, or no supplier) is keyed by
 * its place in the list: order and box number alone collide when one order has two lines in the same box.
 */
export function labelKey(label, index) {
  if (label?.box_code) return label.box_code;
  return `no-code-${index}`;
}

/** The product on a label, so two lines in the same box can be told apart. */
function productText(label) {
  return [label?.color, label?.type].filter(Boolean).join(' ');
}

/** The choices for "Boxes to print": order number, box number and box code, in the order the labels print. */
export function boxLabelOptions(shippingLabel) {
  const labels = shippingLabel?.shipping_label?.orders || [];
  return labels.map((label, index) => {
    const parts = [orderNumberText(label) || 'Order', `Box ${label?.box_number ?? index + 1}`];
    if (productText(label)) parts.push(productText(label));
    if (label?.box_code) parts.push(formatBoxCode(label.box_code));
    return { key: labelKey(label, index), text: parts.join(' · ') };
  });
}

/**
 * The labels to show and print: only the picked ones, or all of them when nothing is picked. PGSS-5824: each order's
 * boxes are counted before picking, so a reprinted box still says 2/3.
 */
export function selectLabels(shippingLabel, pickedKeys) {
  if (!shippingLabel?.shipping_label) return shippingLabel;
  const counted = withBoxCounts(shippingLabel.shipping_label.orders);
  const picked = new Set(pickedKeys || []);
  const orders = picked.size ? counted.filter((label, index) => picked.has(labelKey(label, index))) : counted;
  return { ...shippingLabel, shipping_label: { ...shippingLabel.shipping_label, orders } };
}
